import * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { cn } from "../lib/cn";

export interface UndoChipProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Short label describing the routed action (e.g. "Aperto Docs"). */
  label: React.ReactNode;
  /** Undo button copy. Default `"Annulla"`. */
  actionLabel?: React.ReactNode;
  /** Timer progress 0–100 (drives the radial countdown). */
  progress?: number;
  /** Fired when the user clicks the chip / undo action. */
  onUndo?: () => void;
}

/**
 * UndoChip — STEP C on the v18 mockup.
 *
 * Persistent chip injected in chat after Ando auto-routes a query (e.g.
 * opens a document/canvas without asking). Dismisses in 30 s via the
 * conic-gradient timer ring; click anywhere on the chip to undo.
 *
 * Scaffolding only — pair with a `setInterval` in the consumer to drive
 * `progress` from 100 → 0.
 */
export const UndoChip = React.forwardRef<HTMLButtonElement, UndoChipProps>(
  (
    {
      label,
      actionLabel,
      progress = 100,
      onUndo,
      className,
      type,
      ...rest
    },
    ref
  ) => {
    const { i18n } = useLingui();
    const clamped = Math.max(0, Math.min(100, progress));
    return (
      <button
        ref={ref}
        type={type ?? "button"}
        onClick={onUndo}
        className={cn(
          "inline-flex items-center gap-2 self-start rounded-full border border-hairline bg-card/70 py-1.5 pr-2.5 pl-2 font-medium text-ink-2 text-xs shadow-xs",
          "transition-colors duration-fast hover:border-ando-orange-soft hover:bg-card/90 hover:text-ink-1",
          "animate-undo-in",
          className
        )}
        {...rest}
      >
        <span className="text-accent" aria-hidden>
          {/* Undo glyph — kept inline so consumers can swap freely. */}
          <svg
            width={14}
            height={14}
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="inline-block"
            aria-hidden="true"
            focusable="false"
          >
            <title>Undo</title>
            <path d="M3 7h7a4 4 0 010 8H7" />
            <path d="M6 4L3 7l3 3" />
          </svg>
        </span>
        <span className="whitespace-nowrap text-ink-2">{label}</span>
        <span className="h-[2.5px] w-[2.5px] rounded-full bg-ink-4" aria-hidden />
        <span className="font-semibold text-accent">
          {actionLabel ?? i18n._(msg`Annulla`)}
        </span>
        <span
          aria-hidden
          className="relative ml-0.5 inline-block h-3.5 w-3.5 rounded-full"
          style={{
            background: `conic-gradient(rgba(255,122,26,0.6) ${clamped}%, rgba(0,0,0,0.06) 0)`,
          }}
        >
          <span className="absolute inset-[2px] rounded-full bg-card" />
        </span>
      </button>
    );
  }
);
UndoChip.displayName = "UndoChip";
